import type { LoomAcceptedMeshContract } from "./loomAcceptedMeshDecoder";

const VERIFIED_ACCEPTED_MESH = Symbol("verified Loom accepted mesh");

export type LoomVerifiedAcceptedMeshContract = LoomAcceptedMeshContract & {
  readonly [VERIFIED_ACCEPTED_MESH]: true;
};

export interface LoomAcceptedMeshIntegritySource {
  acceptedGeometry: LoomAcceptedMeshContract;
  exactSurface: {
    positions: Float32Array;
    faces: Uint32Array;
  };
}

export interface LoomAcceptedMeshDerivedRevisions {
  acceptedGeometryRevision: string;
  acceptedTopologyRevision: string;
  exactGeometryRevision: string;
  exactTopologyRevision: string;
}

function unionTopologyMetadata(
  header: NonNullable<LoomAcceptedMeshContract["unionReceipt"]>["header"],
): Record<string, unknown> {
  return {
    algorithmId: header.algorithmId,
    evaluatorId: header.evaluatorId,
    topologyId: header.topologyId,
    definitionRevision: header.definitionRevision,
    surfaceGeometryRevision: header.surfaceGeometryRevision,
    kernelTopologyRevision: header.kernelTopologyRevision,
    indexSpace: header.indexSpace,
    faceSourceNodeIndicesArray: header.faceSourceNodeIndicesArray,
    facePatchIndicesArray: header.facePatchIndicesArray,
    faceCornerParametersArray: header.faceCornerParametersArray,
    sharedSeams: header.sharedSeams,
    pairReceipts: header.pairReceipts,
    qc: header.qc,
  };
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value === "boolean" || typeof value === "string") {
    return JSON.stringify(value);
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("Accepted-mesh revision metadata must be finite");
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map((key) => (
      `${JSON.stringify(key)}:${canonicalJson(record[key])}`
    )).join(",")}}`;
  }
  throw new Error("Accepted-mesh revision metadata contains an unsupported value");
}

function float32Bytes(values: Float32Array): Uint8Array {
  const result = new Uint8Array(values.length * 4);
  const view = new DataView(result.buffer);
  for (let index = 0; index < values.length; index += 1) {
    view.setFloat32(index * 4, values[index]!, true);
  }
  return result;
}

function uint32Bytes(values: Uint32Array): Uint8Array {
  const result = new Uint8Array(values.length * 4);
  const view = new DataView(result.buffer);
  for (let index = 0; index < values.length; index += 1) {
    view.setUint32(index * 4, values[index]!, true);
  }
  return result;
}

async function sha256Revision(parts: readonly Uint8Array[]): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Web Crypto is unavailable for Loom accepted-mesh integrity validation");
  }
  const length = parts.reduce((sum, part) => sum + part.byteLength, 0);
  const canonical = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    canonical.set(part, offset);
    offset += part.byteLength;
  }
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", canonical));
  return `sha256:${Array.from(digest, (value) => value.toString(16).padStart(2, "0")).join("")}`;
}

export async function deriveLoomAcceptedMeshRevisions(
  source: LoomAcceptedMeshIntegritySource,
): Promise<LoomAcceptedMeshDerivedRevisions> {
  const accepted = source.acceptedGeometry;
  const metadata = new TextEncoder().encode(canonicalJson({
    patches: accepted.header.patches,
    sourceNodes: accepted.header.sourceNodes,
    chains: accepted.header.protectedFeatures.chains,
  }));
  const exactTopologyParts = [uint32Bytes(source.exactSurface.faces)];
  const union = accepted.unionReceipt;
  if (union) {
    exactTopologyParts.push(
      uint32Bytes(union.faceSourceNodeIndices),
      uint32Bytes(union.facePatchIndices),
      float32Bytes(union.faceCornerParameters),
    );
    for (const seam of union.sharedSeams) {
      exactTopologyParts.push(
        uint32Bytes(seam.vertexIndices),
        float32Bytes(seam.parametersA),
        float32Bytes(seam.parametersB),
      );
    }
    exactTopologyParts.push(new TextEncoder().encode(
      canonicalJson(unionTopologyMetadata(union.header)),
    ));
  }
  const [acceptedGeometryRevision, acceptedTopologyRevision, exactGeometryRevision, exactTopologyRevision] = await Promise.all([
    sha256Revision([float32Bytes(accepted.positions), float32Bytes(accepted.faceCornerParameters)]),
    sha256Revision([
      uint32Bytes(accepted.faces),
      uint32Bytes(accepted.facePatchIndices),
      uint32Bytes(accepted.faceSourceNodeIndices),
      uint32Bytes(accepted.protectedFeatures.edges),
      uint32Bytes(accepted.protectedFeatures.adjacentFaces),
      uint32Bytes(accepted.protectedFeatures.chainEdgeIndices),
      metadata,
    ]),
    sha256Revision([float32Bytes(source.exactSurface.positions)]),
    sha256Revision(exactTopologyParts),
  ]);
  return { acceptedGeometryRevision, acceptedTopologyRevision, exactGeometryRevision, exactTopologyRevision };
}

function requireRevision(actual: string, expected: string, label: string): void {
  if (actual !== expected) throw new Error(`${label} does not match the decoded accepted-mesh arrays`);
}

export async function verifyLoomAcceptedMeshIntegrity(
  source: LoomAcceptedMeshIntegritySource,
): Promise<LoomVerifiedAcceptedMeshContract> {
  const accepted = source.acceptedGeometry;
  const revisions = await deriveLoomAcceptedMeshRevisions(source);
  requireRevision(accepted.header.geometryRevision, revisions.acceptedGeometryRevision, "acceptedGeometry.geometryRevision");
  requireRevision(accepted.header.topologyRevision, revisions.acceptedTopologyRevision, "acceptedGeometry.topologyRevision");
  requireRevision(
    accepted.header.receipts.panelization.inputGeometryRevision,
    revisions.exactGeometryRevision,
    "acceptedGeometry panelization input geometry revision",
  );
  requireRevision(
    accepted.header.receipts.panelization.inputTopologyRevision,
    revisions.exactTopologyRevision,
    "acceptedGeometry panelization input topology revision",
  );
  const jacobian = accepted.header.receipts.jacobian;
  if (jacobian.status === "stale") {
    requireRevision(jacobian.geometryRevision, revisions.exactGeometryRevision, "stale Jacobian geometry revision");
    requireRevision(jacobian.topologyRevision, revisions.exactTopologyRevision, "stale Jacobian topology revision");
  }
  const union = accepted.unionReceipt;
  if (union) {
    const binding = accepted.header.definitionBinding;
    if (!binding || union.header.algorithmId !== accepted.header.unionAlgorithmId
        || union.header.evaluatorId !== binding.evaluatorId
        || union.header.topologyId !== "selected-wing-fuselage-pairwise-union"
        || union.header.definitionRevision !== binding.definitionRevision) {
      throw new Error("v3 union receipt is not bound to its evaluator, topology, algorithm, and definition");
    }
    requireRevision(
      union.header.surfaceGeometryRevision, revisions.exactGeometryRevision,
      "v3 generalized-union surface geometry revision",
    );
    requireRevision(
      union.header.surfaceTopologyRevision, revisions.exactTopologyRevision,
      "v3 generalized-union surface topology revision",
    );
  }
  const verified = { ...accepted } as LoomVerifiedAcceptedMeshContract;
  Object.defineProperty(verified, VERIFIED_ACCEPTED_MESH, {
    value: true,
    enumerable: false,
    configurable: false,
    writable: false,
  });
  return verified;
}

export function isVerifiedLoomAcceptedMesh(
  value: LoomAcceptedMeshContract,
): value is LoomVerifiedAcceptedMeshContract {
  return (value as Partial<LoomVerifiedAcceptedMeshContract>)[VERIFIED_ACCEPTED_MESH] === true;
}
